import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    useTheme,
    Tooltip,
    InputAdornment,
    Paper,
    Stack,
    Snackbar,
    Breadcrumbs,
    Link,
    Chip,
    CircularProgress,
    MenuItem,
    Card,
    Skeleton,
    Grid,
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    FormControlLabel,
    Autocomplete
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Search,
    ArrowBack,
    NavigateNext,
    HowToVote,
    Person,
    Phone,
    Badge,
    CreditCard,
    Download,
    Upload
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams, GridRowSelectionModel, GridRowId } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useDebounce } from '../../utils/debounce';
import api from '../../api/axios';
import ImportProgressDialog, { ImportPhase } from '../../components/common/ImportProgressDialog';

interface MasterCitizen {
    id: number;
    name: string;
    voter_id: string;
    adhaar_number?: string;
    phone?: string;
    whatsapp_number?: string;
    gender: string;
    age?: number;
    dob?: string;
    community?: string;
    profession?: string;
    income?: string;
    blood_group?: string;
    pro_party?: string;
    is_active_social?: boolean;
    is_nri?: boolean;
    is_resident_satys?: boolean;
    address?: string;
    master_voting_booth_id: number;
}

const MasterVotingBoothDetail = () => {
    const { masterConstituencyId, masterVillageId, masterVotingBoothId } = useParams<{ masterConstituencyId: string, masterVillageId: string, masterVotingBoothId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const theme = useTheme();
    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);

    const handleExport = () => {
        const params = new URLSearchParams();
        params.append('master_voting_booth_id', String(masterVotingBoothId));
        if (search) params.append('search', search);

        // Add other filters if they existed (this page currently only filters by booth + search)

        api.get(`/superadmin/master-citizens/export?${params.toString()}`, { responseType: 'blob' })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `master_citizens_export_${masterVotingBoothId}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch((err) => {
                setSnackbar({
                    open: true,
                    message: 'Failed to export CSV',
                    severity: 'error'
                });
            });
    };

    const [importState, setImportState] = useState<{
        active: boolean;
        fileName: string;
        uploadPct: number;
        phase: ImportPhase;
    }>({ active: false, fileName: '', uploadPct: 0, phase: 'uploading' });

    const handleImportClick = () => {
        // Guard against a second file dialog while one import is already in flight.
        if (importState.active) return;
        document.getElementById('csv-file-input')?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const file = input.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('master_constituency_id', masterConstituencyId || '');
        formData.append('master_village_id', masterVillageId || '');
        formData.append('master_voting_booth_id', masterVotingBoothId || '');

        setImportState({ active: true, fileName: file.name, uploadPct: 0, phase: 'uploading' });

        try {
            const response = await api.post('/superadmin/master-citizens/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (event) => {
                    if (!event.total) return;
                    const uploadPct = Math.round((event.loaded * 100) / event.total);
                    // Once the bytes are all sent the server starts writing rows — and
                    // fanning each one out to every subscribing tenant — which reports
                    // no progress of its own, so switch to the indeterminate phase.
                    setImportState((prev) => ({
                        ...prev,
                        uploadPct,
                        phase: uploadPct >= 100 ? 'processing' : 'uploading',
                    }));
                },
            });
            setSnackbar({
                open: true,
                message: response.data?.message || 'Import completed',
                severity: 'success'
            });
            queryClient.invalidateQueries({ queryKey: ['master-citizens'] });
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Import failed',
                severity: 'error'
            });
        } finally {
            setImportState({ active: false, fileName: '', uploadPct: 0, phase: 'uploading' });
            // Reset input so re-picking the same file fires onChange again
            input.value = '';
        }
    };

    // State
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState<MasterCitizen | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        voter_id: '',
        adhaar_number: '',
        phone: '',
        whatsapp_number: '',
        gender: '',
        age: '',
        dob: '',
        community: '',
        profession: '',
        income: '',
        pro_party: '',
        blood_group: '',
        is_active_social: false,
        is_nri: false,
        is_resident_satys: false,
        address: '',
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<MasterCitizen | null>(null);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

    // Fetch Booth Details
    const { data: booth, isLoading: loadingBooth } = useQuery({
        queryKey: ['master-voting-booth', masterVotingBoothId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/master-voting-booths/${masterVotingBoothId}`);
            return res.data?.data || res.data;
        },
        enabled: !!masterVotingBoothId,
    });

    // Fetch Citizens
    const { data: citizensData, isLoading: loadingCitizens } = useQuery({
        queryKey: ['master-citizens', masterVotingBoothId, pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = {
                master_voting_booth_id: masterVotingBoothId,
                page: pageModel.page + 1,
                per_page: pageModel.pageSize,
                master_constituency_id: masterConstituencyId,

            };
            if (debouncedSearch) params.search = debouncedSearch;
            const res = await api.get('/superadmin/master-citizens', { params });
            return res.data.data;
        },
        enabled: !!masterVotingBoothId,
        placeholderData: keepPreviousData,
    });

    // Fetch Communities Options
    const { data: communitiesOptions } = useQuery({
        queryKey: ['communities-options'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/communities');
            return res.data?.data || [];
        }
    });

    // Fetch Professions Options
    const { data: professionsOptions } = useQuery({
        queryKey: ['professions-options'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/professions');
            return res.data?.data || [];
        }
    });

    // Fetch Political Parties Options
    const { data: politicalPartiesOptions } = useQuery({
        queryKey: ['political-parties-options'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/political-parties');
            return res.data?.data || [];
        }
    });

    const rows = citizensData?.data || [];
    const rowCount = citizensData?.total || 0;

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Create Mutation
    const createMutation = useMutation({
        mutationFn: async (data: any) => api.post('/superadmin/master-citizens', {
            ...data,
            master_constituency_id: Number(masterConstituencyId),
            master_village_id: Number(masterVillageId),
            master_voting_booth_id: Number(masterVotingBoothId),
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-citizens'] });
            queryClient.invalidateQueries({ queryKey: ['master-voting-booth'] });
            handleClose();
            showSnackbar('Citizen created successfully', 'success');
        },
        onError: (err: any) => {
            if (err.response?.status === 422 && err.response?.data?.errors) {
                setFormErrors(err.response.data.errors);
            } else {
                showSnackbar(err.response?.data?.message || 'An error occurred', 'error');
            }
        }
    });

    // Update Mutation
    const updateMutation = useMutation({
        mutationFn: async (data: any) => api.put(`/superadmin/master-citizens/${data.id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-citizens'] });
            handleClose();
            showSnackbar('Citizen updated successfully', 'success');
        },
        onError: (err: any) => {
            showSnackbar(err.response?.data?.message || 'Failed to update citizen', 'error');
        }
    });

    // Delete Mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/superadmin/master-citizens/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-citizens'] });
            queryClient.invalidateQueries({ queryKey: ['master-voting-booth'] });
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            showSnackbar('Citizen deleted successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to delete citizen', 'error')
    });

    // Bulk Delete Mutation
    const bulkDeleteMutation = useMutation({
        mutationFn: async (ids: number[]) => api.post('/superadmin/master-citizens/bulk-delete', { ids }),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({ queryKey: ['master-citizens'] });
            queryClient.invalidateQueries({ queryKey: ['master-voting-booth'] });
            setBulkDeleteDialogOpen(false);
            setSelectedRows({ type: 'include', ids: new Set() });
            showSnackbar(response.data?.message || 'Citizens deleted successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to delete citizens', 'error')
    });

    const handleOpen = (item?: MasterCitizen) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                name: item.name,
                voter_id: item.voter_id || '',
                adhaar_number: item.adhaar_number || '',
                phone: item.phone || '',
                whatsapp_number: item.whatsapp_number || '',
                gender: item.gender || '',
                age: item.age ? String(item.age) : '',
                dob: item.dob || '',
                community: item.community || '',
                profession: item.profession || '',
                income: item.income || '',
                pro_party: item.pro_party || '',
                blood_group: item.blood_group || '',
                is_active_social: !!item.is_active_social,
                is_nri: !!item.is_nri,
                is_resident_satys: !!item.is_resident_satys,
                address: item.address || '',
            });
        } else {
            setEditingItem(null);
            setFormData({
                name: '',
                voter_id: '',
                adhaar_number: '',
                phone: '',
                whatsapp_number: '',
                gender: '',
                age: '',
                dob: '',
                community: '',
                profession: '',
                income: '',
                pro_party: '',
                blood_group: '',
                is_active_social: false,
                is_nri: false,
                is_resident_satys: false,
                address: '',
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({
            name: '',
            voter_id: '',
            adhaar_number: '',
            phone: '',
            whatsapp_number: '',
            gender: '',
            age: '',
            dob: '',
            community: '',
            profession: '',
            income: '',
            pro_party: '',
            blood_group: '',
            is_active_social: false,
            is_nri: false,
            is_resident_satys: false,
            address: '',
        });
    };

    const handleSubmit = () => {
        if (!formData.name) {
            setFormErrors({ ...formErrors, name: 'Name is required' });
            return;
        }

        const payload = {
            ...formData,
            age: formData.age ? Number(formData.age) : null,
            // Ensure booleans are sent correctly
            is_active_social: formData.is_active_social,
            is_nri: formData.is_nri,
            is_resident_satys: formData.is_resident_satys
        };

        if (editingItem) {
            updateMutation.mutate({ ...payload, id: editingItem.id });
        } else {
            createMutation.mutate(payload);
        }
    };

    const handleDeleteClick = (item: MasterCitizen) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (itemToDelete) {
            deleteMutation.mutate(itemToDelete.id);
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr.',
            width: 70,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
        { field: 'voter_id', headerName: 'Voter ID', width: 130 },
        { field: 'gender', headerName: 'Gender', width: 90, renderCell: (params) => <Chip label={params.value} size="small" variant="outlined" /> },
        { field: 'age', headerName: 'Age', width: 70 },
        { field: 'phone', headerName: 'Phone', width: 120 },
        { field: 'whatsapp_number', headerName: 'WhatsApp', width: 120 },
        { field: 'adhaar_number', headerName: 'Aadhaar', width: 130 },
        { field: 'community', headerName: 'Community', width: 120 },
        { field: 'profession', headerName: 'Profession', width: 120 },
        { field: 'income', headerName: 'Income', width: 100 },
        { field: 'dob', headerName: 'DOB', width: 100 },
        { field: 'blood_group', headerName: 'Blood', width: 70 },
        {
            field: 'is_active_social',
            headerName: 'Active Social',
            width: 100,
            renderCell: (params) => params.value ? <Chip label="Yes" size="small" color="success" /> : <Chip label="No" size="small" variant="outlined" />
        },
        {
            field: 'is_nri',
            headerName: 'NRI',
            width: 70,
            renderCell: (params) => params.value ? <Chip label="Yes" size="small" color="info" /> : <Chip label="No" size="small" variant="outlined" />
        },
        {
            field: 'is_resident_satys',
            headerName: 'Resident Stays',
            width: 110,
            renderCell: (params) => params.value ? <Chip label="Yes" size="small" color="warning" /> : <Chip label="No" size="small" variant="outlined" />
        },
        { field: 'address', headerName: 'Address', width: 150 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpen(params.row)} color="primary">
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteClick(params.row)} color="error">
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Breadcrumbs */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
                    <Link component={RouterLink} color="inherit" to="/super-admin/master-constituencies" sx={{ display: 'flex', alignItems: 'center' }}>
                        Master Constituencies
                    </Link>
                    <Link component={RouterLink} color="inherit" to={`/super-admin/master-constituencies/${masterConstituencyId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                        {booth?.constituency?.name || 'Constituency'}
                    </Link>
                    <Link component={RouterLink} color="inherit" to={`/super-admin/master-constituencies/${masterConstituencyId}/villages/${masterVillageId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                        {booth?.village?.name || 'Village'}
                    </Link>
                    <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                        {booth?.name || 'Loading...'}
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)', // Indigo gradient
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate(`/super-admin/master-constituencies/${masterConstituencyId}/villages/${masterVillageId}`)}
                            sx={{ color: 'white' }}
                        >
                            <ArrowBack />
                        </IconButton>
                        {booth?.name || <Skeleton width={200} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, opacity: 0.9, ml: 7 }}>
                        Manage citizens in this voting booth
                    </Typography>
                    <Chip
                        icon={<Person style={{ color: 'white' }} />}
                        label={`${rowCount} Citizens`}
                        variant="outlined"
                        sx={{ mt: 2, ml: 7, color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleExport}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderColor: 'rgba(255,255,255,0.3)',
                            color: 'white',
                            '&:hover': {
                                borderColor: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)'
                            },
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={importState.active
                            ? <CircularProgress size={18} color="inherit" />
                            : <Upload />}
                        onClick={handleImportClick}
                        disabled={importState.active}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderColor: 'rgba(255,255,255,0.3)',
                            color: 'white',
                            '&:hover': {
                                borderColor: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)'
                            },
                            // Keep the disabled state legible against the gradient header,
                            // where MUI's default grey would all but disappear.
                            '&.Mui-disabled': {
                                color: 'rgba(255,255,255,0.6)',
                                borderColor: 'rgba(255,255,255,0.2)'
                            },
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                    >
                        {importState.active ? 'Importing…' : 'Import CSV'}
                    </Button>
                    <input
                        type="file"
                        id="csv-file-input"
                        style={{ display: 'none' }}
                        accept=".csv"
                        onChange={handleFileChange}
                    />

                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpen()}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderRadius: 2,
                            fontWeight: 'bold',
                            bgcolor: 'white',
                            color: '#4f46e5',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                        }}
                    >
                        Add Citizen
                    </Button>
                </Box>
            </Paper >

            {/* Controls Section */}
            < Card sx={{ mb: 3 }}>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                        placeholder="Search citizens..."
                        variant="outlined"
                        size="small"
                        sx={{ flex: 1 }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {selectedRows.ids.size > 0 && (
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => setBulkDeleteDialogOpen(true)}
                        >
                            Delete Selected ({selectedRows.ids.size})
                        </Button>
                    )}
                </Box>
            </Card >

            {/* DataGrid */}
            < Card sx={{ height: 600, width: '100%' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loadingCitizens}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[25, 50, 100]}
                    paginationMode="server"
                    rowCount={rowCount}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={(newSelection) => {
                        if (newSelection.type === 'exclude') {
                            const currentIds = new Set<GridRowId>(rows.map((row: any) => row.id));
                            setSelectedRows({ type: 'include', ids: currentIds });
                        } else {
                            setSelectedRows(newSelection);
                        }
                    }}
                    sx={{ border: 0 }}
                />
            </Card >

            {/* Create/Edit Dialog */}
            < Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth >
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingItem ? 'Edit Citizen' : 'Add New Citizen'}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                autoFocus
                                label="Full Name"
                                fullWidth
                                variant="outlined"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Voter ID"
                                fullWidth
                                variant="outlined"
                                value={formData.voter_id}
                                onChange={(e) => setFormData({ ...formData, voter_id: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth error={!!formErrors.gender}>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    value={formData.gender}
                                    label="Gender"
                                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                                >
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Age"
                                fullWidth
                                type="number"
                                variant="outlined"
                                value={formData.age}
                                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Phone Number"
                                fullWidth
                                variant="outlined"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Aadhaar Number"
                                fullWidth
                                variant="outlined"
                                value={formData.adhaar_number}
                                onChange={(e) => setFormData({ ...formData, adhaar_number: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete
                                freeSolo
                                options={communitiesOptions?.map((c: any) => c.name) || []}
                                value={formData.community}
                                onChange={(event, newValue) => {
                                    setFormData({ ...formData, community: newValue || '' });
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setFormData({ ...formData, community: newInputValue });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Community"
                                        fullWidth
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="WhatsApp Number"
                                fullWidth
                                variant="outlined"
                                value={formData.whatsapp_number}
                                onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete
                                freeSolo
                                options={professionsOptions?.map((p: any) => p.name) || []}
                                value={formData.profession}
                                onChange={(event, newValue) => {
                                    setFormData({ ...formData, profession: newValue || '' });
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setFormData({ ...formData, profession: newInputValue });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Profession"
                                        fullWidth
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Income"
                                fullWidth
                                variant="outlined"
                                value={formData.income}
                                onChange={(e) => setFormData({ ...formData, income: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Blood Group</InputLabel>
                                <Select
                                    value={formData.blood_group}
                                    label="Blood Group"
                                    onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="A+">A+</MenuItem>
                                    <MenuItem value="A-">A-</MenuItem>
                                    <MenuItem value="B+">B+</MenuItem>
                                    <MenuItem value="B-">B-</MenuItem>
                                    <MenuItem value="AB+">AB+</MenuItem>
                                    <MenuItem value="AB-">AB-</MenuItem>
                                    <MenuItem value="O+">O+</MenuItem>
                                    <MenuItem value="O-">O-</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Date of Birth"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={formData.dob}
                                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Address"
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.is_active_social}
                                            onChange={(e) => setFormData({ ...formData, is_active_social: e.target.checked })}
                                        />
                                    }
                                    label="Active Socially"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.is_nri}
                                            onChange={(e) => setFormData({ ...formData, is_nri: e.target.checked })}
                                        />
                                    }
                                    label="NRI"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.is_resident_satys}
                                            onChange={(e) => setFormData({ ...formData, is_resident_satys: e.target.checked })}
                                        />
                                    }
                                    label="Resident Stays"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} sx={{ borderRadius: 2, textTransform: 'none' }} size="large">Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={createMutation.isPending || updateMutation.isPending}
                        sx={{ borderRadius: 2, px: 4, textTransform: 'none' }}
                        size="large"
                    >
                        {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Citizen'}
                    </Button>
                </DialogActions>
            </Dialog >

            {/* Delete Confirmation Dialog */}
            < Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>"{itemToDelete?.name}"</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog >

            {/* Bulk Delete Confirmation Dialog */}
            < Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Bulk Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>{selectedRows.ids.size} citizen(s)</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={() => setBulkDeleteDialogOpen(false)} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={() => bulkDeleteMutation.mutate(Array.from(selectedRows.ids) as number[])} disabled={bulkDeleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {bulkDeleteMutation.isPending ? 'Deleting...' : `Delete ${selectedRows.ids.size} Citizens`}
                    </Button>
                </DialogActions>
            </Dialog >

            {/* CSV import progress */}
            <ImportProgressDialog
                open={importState.active}
                fileName={importState.fileName}
                uploadPct={importState.uploadPct}
                phase={importState.phase}
                title="Importing master citizens"
            />

            {/* Snackbar Messages */}
            < Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar >
        </Box >
    );
};

export default MasterVotingBoothDetail;
